'use client';

import { Box, Card, Skeleton } from '@mui/material';

const DesktopItemSkeleton = () => (
  <Card
    sx={{
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      gap: { xs: 2, sm: 3 },
      p: { xs: 2, sm: 3 },
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '12px',
      boxShadow: 'none',
    }}
  >
    {/* Date Box Skeleton */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: { xs: 50, sm: 60 },
        flexShrink: 0,
      }}
    >
      <Skeleton variant="text" width={32} height={40} />
      <Skeleton variant="text" width={40} height={20} />
    </Box>

    {/* Avatar Skeleton */}
    <Skeleton
      variant="rounded"
      width={80}
      height={80}
      sx={{ borderRadius: '8px', flexShrink: 0 }}
    />

    {/* Details Skeleton */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={80} height={20} />
      </Box>
    </Box>

    {/* Options Box Skeleton - Parallel to Content on Desktop */}
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Skeleton variant="rounded" width={70} height={28} sx={{ borderRadius: '6px' }} />
      <Skeleton variant="rounded" width={70} height={28} sx={{ borderRadius: '6px' }} />
    </Box>

    {/* Button Skeleton */}
    <Skeleton
      variant="rounded"
      width={100}
      height={40}
      sx={{ borderRadius: '8px', flexShrink: 0 }}
    />
  </Card>
);

const MobileItemSkeleton = () => (
  <Card
    sx={{
      display: { xs: 'flex', md: 'none' },
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.5,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      boxShadow: 'none',
    }}
  >
    {/* Date Box Skeleton - Left Side */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 48,
        flexShrink: 0,
        pt: 0.5,
      }}
    >
      <Skeleton variant="text" width={28} height={32} />
      <Skeleton variant="text" width={32} height={16} />
    </Box>

    {/* Content Skeleton - Middle */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />

      {/* Time & Volume row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <Skeleton variant="rounded" width={56} height={24} sx={{ borderRadius: '6px' }} />
        <Skeleton variant="text" width={50} height={20} />
      </Box>

      {/* Options row */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Skeleton variant="rounded" width={56} height={28} sx={{ borderRadius: '6px' }} />
        <Skeleton variant="rounded" width={56} height={28} sx={{ borderRadius: '6px' }} />
      </Box>
    </Box>

    {/* Avatar Skeleton - Right Side */}
    <Skeleton variant="rounded" width={64} height={64} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
  </Card>
);

interface MentionsSkeletonProps {
  count?: number;
}

const SKELETON_ITEMS = [1, 2, 3, 4, 5, 6];

export const MentionsSkeleton = ({ count = 6 }: MentionsSkeletonProps) => {
  const items = count === 6 ? SKELETON_ITEMS : Array.from({ length: count }, (_, i) => i + 1);

  const renderSkeletonItem = (itemKey: number) => {
    return (
      <Box key={itemKey}>
        <DesktopItemSkeleton />
        <MobileItemSkeleton />
      </Box>
    );
  };

  const renderedItems = items.map(renderSkeletonItem);

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{renderedItems}</Box>;
};
